import { useRuntimeConfig, useRoute, useCookie, navigateTo } from '#imports'
import { useAuth } from '~/composables/useAuth'

export const getJwtToken = () => {
  try {
    const token = useCookie('authToken')
    return token.value ?? ''
  } catch (e) {
    console.log('>>>>error', e)
    return ''
  }
}

export const clearCookieAndRedirectToLogin = () => {
  const { clearCookie } = useAuth()
  clearCookie()
  return navigateTo('/login')
}

const pathsNotToRedirect = ['signup', 'login', 'forgotPassword']
const checkResponse = (response: Response) => {
  if (response.status === 401) {
    const route = useRoute()
    if (
      pathsNotToRedirect.every(
        (path) =>
          !route.path.toLocaleLowerCase().includes(path.toLocaleLowerCase()),
      )
    ) {
      const { clearCookie } = useAuth()
      clearCookie()
      navigateTo('/login')
    }
  }

  // 402 = subscription_required. Route the user to the one page that can fix
  // it (identity/billing endpoints are exempt from the subscription gate, so
  // the destination always renders).
  if (response.status === 402) {
    const route = useRoute()
    if (!route.path.startsWith('/organizationSettings')) {
      navigateTo('/organizationSettings?tab=administration')
    }
  }
}

/**
 * Standard API error: `message` carries the human-readable reason from the
 * backend body; `status` and `response.data` carry the coded shape
 * (e.g. { error: 'seat_limit_reached', ... }).
 *
 * Use this from API modules instead of `throw new Error("HTTP error! ...")` —
 * bare status throws are how backend guard messages ("Cannot remove the last
 * admin", seat-limit details) silently turned into generic failure toasts.
 */
export type ApiError = Error & {
  status: number
  response: { status: number; data: any }
}

export async function throwApiError(response: Response): Promise<never> {
  const data = await response.json().catch(() => ({}))
  const err = new Error(
    data.message || data.error || `HTTP error! status: ${response.status}`,
  ) as ApiError
  err.status = response.status
  err.response = { status: response.status, data }
  throw err
}

/**
 * The reason to actually show a customer, or `fallback` when there isn't one.
 *
 * A generic catch message is a bug factory: the backend explains exactly why an
 * action failed ("You've used all 5 of your seats"), and overwriting that with
 * "Please try again" turns a solvable problem into a mystery. Reads both the
 * fetch shape (err.message, set by throwApiError) and the axios-ish shape
 * (err.response.data.message) so it works regardless of who threw.
 *
 * Bare transport strings are treated as no reason at all — the fallback is more
 * useful to a human than "HTTP error! status: 500".
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const err = error as Partial<ApiError> & { response?: { data?: any } }
  const fromBody = err?.response?.data?.message || err?.response?.data?.error
  const candidate = (fromBody || err?.message || '').toString().trim()

  if (
    !candidate ||
    candidate.startsWith('HTTP error!') ||
    candidate === 'Failed to fetch'
  ) {
    return fallback
  }
  return candidate
}

// Define an options interface for the API methods
interface ApiRequestOptions {
  isPublic?: boolean // If true, Authorization header will be omitted
  // Background/optional requests must not redirect the current page when an
  // entitlement check rejects them. The caller still receives the response.
  suppressResponseRedirects?: boolean
  signal?: AbortSignal
  // Add other potential options here in the future if needed
}

function useApi() {
  const getBaseURL = () => {
    if (!import.meta.client) return ''
    try {
      return useRuntimeConfig().public.apiBaseUrl
    } catch {
      return ''
    }
  }

  async function get(
    endpoint: string,
    queryParms: { [key: string]: string } = {},
    options?: ApiRequestOptions, // Added options
  ) {
    // eslint-disable-next-line no-useless-catch
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      }
      if (!options?.isPublic) {
        const token = getJwtToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      }

      const params = new URLSearchParams()
      Object.entries(queryParms).forEach(([key, value]) => {
        params.append(key, value)
      })
      const finalQueryString = params.toString() ? `?${params.toString()}` : ''

      const response = await fetch(
        `${getBaseURL()}${endpoint}${finalQueryString}`,
        {
          method: 'GET',
          headers,
        },
      )
      if (!options?.suppressResponseRedirects) checkResponse(response)
      return response
    } catch (err) {
      console.error('error', err)
      throw err
    }
  }

  async function post(
    endpoint: string,
    payload: object = {},
    options?: ApiRequestOptions,
  ) {
    // Added options
    // eslint-disable-next-line no-useless-catch
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      }
      if (!options?.isPublic) {
        // Check if the call is public
        const token = getJwtToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      }

      const response = await fetch(`${getBaseURL()}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        signal: options?.signal,
      })
      if (!options?.suppressResponseRedirects) checkResponse(response)
      return response
    } catch (err) {
      console.error('error', err)
      throw err
    }
  }

  async function put(
    endpoint: string,
    payload: object = {},
    options?: ApiRequestOptions,
  ) {
    // Added options
    // eslint-disable-next-line no-useless-catch
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      }
      if (!options?.isPublic) {
        const token = getJwtToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      }

      const response = await fetch(`${getBaseURL()}${endpoint}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      })
      checkResponse(response)
      return response
    } catch (err) {
      console.error('error', err)
      throw err
    }
  }

  async function patch(
    endpoint: string,
    payload: object = {},
    options?: ApiRequestOptions,
  ) {
    // Added options
    // eslint-disable-next-line no-useless-catch
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      }
      if (!options?.isPublic) {
        const token = getJwtToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      }
      const response = await fetch(`${getBaseURL()}${endpoint}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(payload),
      })
      checkResponse(response)
      return response
    } catch (err) {
      console.error('error', err)
      throw err
    }
  }

  async function del(endpoint: string, options?: ApiRequestOptions) {
    // Added options
    // eslint-disable-next-line no-useless-catch
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      }
      if (!options?.isPublic) {
        const token = getJwtToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      }
      const response = await fetch(`${getBaseURL()}${endpoint}`, {
        method: 'DELETE',
        headers,
      })
      checkResponse(response)

      return response
    } catch (err) {
      console.error('error', err)
      throw err
    }
  }
  return {
    get,
    post,
    put,
    patch,
    del,
  }
}

export default useApi
